import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getSearchSuggestions, clearSearchSuggestions } from 'actions/index';
import { push } from 'modules/history';
import { Wrapper, BarShell, SearchIcon, Chip, ChipLabel, ChipClose, Input } from './styled';
import SuggestionPanel from './SuggestionPanel';

const DEFAULT_TYPES = ['brand', 'account', 'location', 'city', 'contact'];
const DEBOUNCE_MS = 250;
const CHIP_TYPES_SINGLE = new Set(['brand', 'account', 'city', 'state', 'installed']);

// maps chip.type → location-list API filter keys.
const DEFAULT_CHIP_FILTER_MAP = {
  brand: chip => ({ brand_id: chip.id }),
  account: chip => ({ account_id: chip.id }),
  city: chip => ({ city_id: chip.id }),
  state: chip => ({ state: chip.id }),
  contact: chip => ({ contact: chip.value || chip.label }),
  installed: chip => ({ installed: chip.value }),
};

export function chipsToFilters(chips, map = DEFAULT_CHIP_FILTER_MAP) {
  return (chips || []).reduce((acc, chip) => {
    const mapper = map[chip.type];
    if (!mapper) return acc;
    return { ...acc, ...mapper(chip) };
  }, {});
}

class SmartSearchBar extends Component {
  constructor(props) {
    super(props);
    this.state = {
      focused: false,
      activeIndex: 0,
      anchorRect: null,
    };
    this.inputRef = React.createRef();
    this.barRef = React.createRef();
    this.debounceTimer = null;
    this.anchorRaf = null;
    this.suppressNextBlur = false;
  }

  componentDidMount() {
    this.maybeFetchSuggestions(this.props.text, this.props.chips);
    window.addEventListener('resize', this.updateAnchorRect);
    window.addEventListener('scroll', this.updateAnchorRect, true);
  }

  componentDidUpdate(prevProps) {
    const chipsChanged =
      (prevProps.chips || []).length !== (this.props.chips || []).length ||
      (prevProps.chips || []).some((c, i) => {
        const n = (this.props.chips || [])[i];
        return !n || c.type !== n.type || c.id !== n.id || c.value !== n.value;
      });
    if (chipsChanged) {
      this.maybeFetchSuggestions(this.props.text, this.props.chips);
    }
  }

  componentWillUnmount() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    if (this.anchorRaf) cancelAnimationFrame(this.anchorRaf);
    window.removeEventListener('resize', this.updateAnchorRect);
    window.removeEventListener('scroll', this.updateAnchorRect, true);
    this.props.clearSuggestions();
  }

  maybeFetchSuggestions = (text, chips) => {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    const trimmed = (text || '').trim();
    if (trimmed.length < 2) {
      this.props.clearSuggestions();
      return;
    }
    this.debounceTimer = setTimeout(() => {
      const context = chipsToFilters(chips || [], this.getFilterMap());
      this.props.fetchSuggestions({
        q: trimmed,
        types: (this.props.types || DEFAULT_TYPES).join(','),
        ...context,
      });
    }, DEBOUNCE_MS);
  };

  getFilterMap() {
    return { ...DEFAULT_CHIP_FILTER_MAP, ...(this.props.chipFilterMap || {}) };
  }

  getTypes() {
    return this.props.types && this.props.types.length ? this.props.types : DEFAULT_TYPES;
  }

  updateAnchorRect = () => {
    if (!this.barRef.current) return;
    if (this.anchorRaf) cancelAnimationFrame(this.anchorRaf);
    this.anchorRaf = requestAnimationFrame(() => {
      const rect = this.barRef.current.getBoundingClientRect();
      this.setState({ anchorRect: rect });
    });
  };

  onFocus = () => {
    this.setState({ focused: true }, this.updateAnchorRect);
  };

  onBlur = () => {
    // give dropdown clicks a chance to register before we close
    if (this.suppressNextBlur) {
      this.suppressNextBlur = false;
      return;
    }
    setTimeout(() => {
      if (!this.barRef.current) return;
      if (document.activeElement === this.inputRef.current) return;
      this.setState({ focused: false });
    }, 120);
  };

  onInputChange = event => {
    const text = event.target.value;
    this.setState({ activeIndex: 0 });
    this.props.onChange({ chips: this.props.chips || [], text });
    this.maybeFetchSuggestions(text, this.props.chips || []);
  };

  buildFlatItems() {
    const types = this.getTypes();
    const { suggestions } = this.props;
    const groups = [];
    const flat = [];
    const buckets = {
      brand: suggestions.brands || [],
      account: suggestions.accounts || [],
      location: suggestions.locations || [],
      city: suggestions.cities || [],
      contact: suggestions.contacts || [],
    };
    types.forEach(type => {
      const raws = buckets[type] || [];
      if (raws.length === 0) return;
      const items = raws.map(raw => {
        const item = { ...raw, type, __flatIndex: flat.length };
        flat.push(item);
        return item;
      });
      groups.push({ type, items });
    });
    return { groups, flat };
  }

  onKeyDown = event => {
    const { flat } = this.buildFlatItems();
    const { activeIndex } = this.state;

    if (event.key === 'Backspace' && !this.props.text && (this.props.chips || []).length) {
      event.preventDefault();
      const chips = (this.props.chips || []).slice(0, -1);
      this.props.onChange({ chips, text: '' });
      return;
    }

    if (!flat.length) {
      if (event.key === 'Escape') this.setState({ focused: false });
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.setState({ activeIndex: (activeIndex + 1) % flat.length });
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.setState({ activeIndex: (activeIndex - 1 + flat.length) % flat.length });
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const item = flat[activeIndex];
      if (item) this.pickSuggestion(item);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.setState({ focused: false });
    }
  };

  pickSuggestion = item => {
    const { navigateOnRecord, chips: existingChips = [] } = this.props;
    // preventDefault on the panel mousedown already kept focus, so the matching
    // blur never fires; clear the guard here so it can't eat a later real blur.
    this.suppressNextBlur = false;

    if (item.type === 'location' && navigateOnRecord !== false && item.navigateTo) {
      push(item.navigateTo);
      this.props.onChange({ chips: existingChips, text: '' });
      this.props.clearSuggestions();
      this.setState({ focused: false });
      return;
    }

    const newChip = {
      type: item.type,
      id: item.id,
      value: item.value,
      label: item.label,
      subtitle: item.subtitle,
    };

    let nextChips;
    if (CHIP_TYPES_SINGLE.has(item.type)) {
      nextChips = existingChips.filter(c => c.type !== item.type).concat(newChip);
    } else {
      const isDuplicate = existingChips.some(
        c =>
          c.type === newChip.type &&
          ((newChip.id && c.id === newChip.id) || (newChip.value && c.value === newChip.value)),
      );
      nextChips = isDuplicate ? existingChips : existingChips.concat(newChip);
    }

    this.setState({ activeIndex: 0 });
    this.props.onChange({ chips: nextChips, text: '' });
    this.props.clearSuggestions();
    if (this.inputRef.current) this.inputRef.current.focus();
  };

  removeChip = chipToRemove => {
    this.suppressNextBlur = false;
    const chips = (this.props.chips || []).filter(
      c =>
        !(
          c.type === chipToRemove.type &&
          c.id === chipToRemove.id &&
          c.value === chipToRemove.value
        ),
    );
    this.props.onChange({ chips, text: this.props.text || '' });
  };

  renderChip = (chip, idx) => (
    <Chip key={`${chip.type}-${chip.id || chip.value}-${idx}`}>
      <ChipLabel>
        <strong style={{ fontWeight: 600, textTransform: 'capitalize', marginRight: 4 }}>
          {chip.type}:
        </strong>
        {chip.label}
      </ChipLabel>
      <ChipClose
        type="button"
        onMouseDown={e => {
          e.preventDefault();
          this.suppressNextBlur = true;
        }}
        onClick={() => this.removeChip(chip)}
        aria-label={`Remove ${chip.type} filter`}
      >
        ×
      </ChipClose>
    </Chip>
  );

  render() {
    const { chips = [], text = '', placeholder, loading } = this.props;
    const { focused, activeIndex, anchorRect } = this.state;
    const { groups, flat } = this.buildFlatItems();
    const showPanel = focused;

    return (
      <Wrapper>
        <BarShell
          focused={focused}
          ref={this.barRef}
          onClick={() => this.inputRef.current && this.inputRef.current.focus()}
        >
          <SearchIcon className="fas fa-search" />
          {chips.map(this.renderChip)}
          <Input
            ref={this.inputRef}
            value={text}
            placeholder={chips.length ? '' : placeholder || 'Search brands, accounts, locations…'}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            onChange={this.onInputChange}
            onKeyDown={this.onKeyDown}
          />
        </BarShell>
        {showPanel && (
          <SuggestionPanel
            anchorRect={anchorRect}
            groups={groups}
            flatItems={flat}
            activeIndex={activeIndex}
            loading={loading}
            query={text}
            onPick={this.pickSuggestion}
            onHover={index => this.setState({ activeIndex: index })}
            onMouseDownCapture={e => {
              // keep input focused when clicking inside the panel
              e.preventDefault();
              this.suppressNextBlur = true;
            }}
          />
        )}
      </Wrapper>
    );
  }
}

SmartSearchBar.propTypes = {
  chipFilterMap: PropTypes.object,
  chips: PropTypes.array,
  clearSuggestions: PropTypes.func.isRequired,
  fetchSuggestions: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  navigateOnRecord: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  suggestions: PropTypes.object,
  text: PropTypes.string,
  types: PropTypes.arrayOf(PropTypes.string),
};

SmartSearchBar.defaultProps = {
  chips: [],
  text: '',
  navigateOnRecord: true,
  suggestions: { brands: [], accounts: [], locations: [], cities: [], contacts: [] },
};

const mapStateToProps = state => ({
  suggestions: (state.search && state.search.suggestions) || {
    brands: [],
    accounts: [],
    locations: [],
    cities: [],
    contacts: [],
  },
  loading: !!(state.search && state.search.loading),
});

const mapDispatchToProps = dispatch => ({
  fetchSuggestions: params => dispatch(getSearchSuggestions(params)),
  clearSuggestions: () => dispatch(clearSearchSuggestions()),
});

export default connect(mapStateToProps, mapDispatchToProps)(SmartSearchBar);
